import type { Guide, GuideStep } from "@/types/api";

export type PlaybackStep = GuideStep & {
  audio_url?: string;
  note?: { title: string; body: string; link: { label: string; href: string } };
};
export type PlaybackGuide = Omit<Guide, "steps"> & {
  steps: PlaybackStep[];
  progressVersion?: string;
};

// The September 2026 editor delivery has its own step boundaries. Keep the
// copy, silent loops, and once-per-step narration together as one version.
// These assets ship with the portal; no production storage objects are replaced.
const content = [
  {
    title: "Gather your supplies",
    body: "Lay out your vial, a sterile syringe, alcohol swabs, and a sharps container on a clean surface. Follow your clinician’s instructions and prescription label.",
    transcript:
      "This is a general walkthrough for preparing and administering your first injection with System. Follow along at your own pace. Always go by the specific instructions your clinician gave you and what’s on your prescription label, since your dose and technique may be set up just for you.\n\nStart by laying everything out on a clean surface. You’ll want your vial, a sterile syringe, alcohol swabs, and your sharps container all within reach before you begin.",
  },
  {
    title: "Wash your hands",
    body: "Wash and dry your hands thoroughly before touching your supplies.",
    transcript:
      "Wash your hands thoroughly and dry them completely before you touch any of your supplies. This is the most important step for keeping everything clean and preventing contamination.",
  },
  {
    title: "Wipe the vial",
    body: "Wipe the rubber stopper with an alcohol swab, then let it air dry for about 30 seconds.",
    transcript:
      "Take an alcohol swab and wipe the rubber stopper on your vial firmly. Let it air dry for about 30 seconds before you go any further.",
  },
  {
    title: "Remove the syringe cap",
    body: "Remove the plastic cap without touching the needle.",
    transcript:
      "Pull the plastic cap off your syringe to expose the needle. Try to keep from touching the needle itself as you do this.",
  },
  {
    title: "Draw air into the syringe",
    body: "Pull back the plunger to draw in an amount of air equal to your dose.",
    transcript:
      "Pull back the plunger to draw in an amount of air equal to your dose.",
  },
  {
    title: "Push the air into the vial",
    body: "Insert the needle into the vial and push in the air to equalize the pressure.",
    transcript:
      "Then insert the needle into the vial and push that air in. This equalizes the pressure inside and makes the next step easier.",
  },
  {
    title: "Draw your prescribed dose",
    body: "Turn the vial upside down, keeping the needle tip below the liquid line. Draw your prescribed dose, then remove the syringe.",
    transcript:
      "Turn the vial upside down and keep the needle tip below the liquid line. Slowly pull back the plunger to draw up your prescribed dose and remove the syringe from the vial.",
  },
  {
    title: "Flick the syringe",
    body: "Hold the syringe upright and gently flick the barrel to move air bubbles toward the needle.",
    transcript:
      "Hold the syringe upright and gently flick the barrel a few times. This sends any air bubbles up toward the needle.",
  },
  {
    title: "Push out air bubbles",
    body: "Slowly press the plunger until you see a small bead of liquid at the needle tip.",
    transcript:
      "Slowly press the plunger to push those bubbles toward the needle. Stop when you see a small bead of liquid at the tip of the needle.",
  },
  {
    title: "Choose your injection site",
    body: "Rotate sites between sessions to avoid irritation. Clean your chosen area with a fresh alcohol swab.",
    transcript:
      "Pick your site and rotate between sessions to avoid irritation. Common areas are the stomach or the thigh. Wipe the area you choose with a fresh alcohol swab.",
  },
  {
    title: "Inject and dispose of the syringe",
    body: "Inject as directed by your clinician, then immediately place the used syringe in a sharps container.",
    note: {
      title: "No sharps container?",
      body: "Use an empty, heavy-duty plastic container, such as a laundry detergent bottle, with a tight-fitting, puncture-resistant lid. It must resist leaks and stay upright. Label it “SHARPS — DO NOT RECYCLE” and keep it away from children and pets. Never put loose needles in trash or recycling. Seal the container when it’s ¾ full and follow your local sharps-disposal rules.",
      link: {
        label: "FDA sharps-disposal guidance",
        href: "https://www.fda.gov/medical-devices/safely-using-sharps-needles-and-syringes-home-work-and-travel/sharps-disposal-containers",
      },
    },
    transcript:
      "Hold the syringe, pick a fold of skin, and bring the needle in at a 45-degree angle for a subcutaneous injection. Insert the needle quickly, then slowly press the plunger to deliver the full dose. Then withdraw the needle.\n\nPlace the used syringe straight into your sharps container. That’s it! If anything looks off with your vial or you’re ever unsure about a step, don’t guess. Reach out to your clinical team and we will walk you through it.",
  },
];

export const SELF_INJECTION_STEPS: PlaybackStep[] = content.map((step, i) => {
  const path = `/videos/self-injection/v2/step-${String(i + 1).padStart(2, "0")}`;
  return {
    ...step,
    index: i + 1,
    media_url: `${path}.mp4`,
    audio_url: `${path}.m4a`,
  };
});

export function withGuideMedia(guide: Guide): PlaybackGuide {
  if (guide.slug !== "self-injection") return guide;
  return {
    ...guide,
    steps: SELF_INJECTION_STEPS,
    total_steps: SELF_INJECTION_STEPS.length,
    progressVersion: "v2",
  };
}
